import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Text,
  Select,
  Box,
  UploadMediaContainer,
  ModalContext,
} from "dash-ui";

import { useUploadMediaFile } from "common/hooks/useUploadMediaFile";
import {
  useAbortAllUploadsMutation,
  useAbortUploadFileMutation,
  BrandingTypeEnum,
  FileUploadInput,
} from "gql/generated.graphql";
import { getBrandingTypeOptions } from "../../helpers/brandingTypeOptions";

interface IUploadMediaWithBrandingModalProps {
  propertyId: string;
  folderId?: string;
}

const UploadMediaWithBrandingModal = ({
  propertyId,
  folderId,
}: IUploadMediaWithBrandingModalProps) => {
  const { t } = useTranslation(["pages", "common"]);
  const { toggleModal, isOpenCurrent } = useContext(ModalContext);
  const isOpen = !!isOpenCurrent["UploadMediaWithBrandingModal"];
  const [brandingType, setBrandingType] = useState<
    BrandingTypeEnum | undefined
  >(BrandingTypeEnum.Unbranded);
  const uploadMediaFile = useUploadMediaFile();

  const [abortUpload] = useAbortUploadFileMutation();
  const [abortAllUploads] = useAbortAllUploadsMutation();

  const brandingOptions = getBrandingTypeOptions(t);

  const handleUpload = (filesToUpload: FileUploadInput[]) => {
    filesToUpload.forEach((fileUpload) => {
      uploadMediaFile({
        fileUpload,
        propertyId,
        mediaFolderId: folderId,
        brandingType: brandingType || BrandingTypeEnum.Unbranded,
      });
    });
  };

  const handleClose = () => {
    toggleModal(false, "UploadMediaWithBrandingModal");
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      title={t("pages:propertyPage.uploadMedia") as string}
    >
      <Box>
        {/* Branding Type Selector */}
        <Box px={4} py={3}>
          <Box mb={2}>
            <Text medium bold>
              {t("common:labels.brandingType")}
            </Text>
          </Box>
          <Select
            w="100%"
            value={brandingType}
            onChanged={(value?: string) =>
              setBrandingType(value as BrandingTypeEnum)
            }
            options={brandingOptions}
            isSearchable={false}
            placeholder={t("common:placeholders.selectBrandingType") || ""}
          />
        </Box>

        {/* Upload Container */}
        <Box>
          <UploadMediaContainer
            visible={true}
            onUpload={handleUpload}
            onClose={handleClose}
            cancelUpload={(trackingId: string = "") =>
              abortUpload({
                variables: { progressTrackingId: trackingId },
              })
            }
            cancelAllUploads={abortAllUploads}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadMediaWithBrandingModal;
